import React, { useState, useEffect } from 'react';
import { Button, InputNumber, Select, Space, Switch } from 'antd';
import {
  CAMERA_FLY_PRESET_TYPES,
  DEFAULT_PRESET_ADVANCED_SETTINGS,
} from '../../../types';
import { EASING_TYPES } from 'util/constants';
import styles from '../../../event-timeline.module.css';

const { Option } = Select;

interface MoveParamsProps {
  nodeId: string;
  params: any;
  setParams: (params: any) => void;
}

const FLY_PRESETS = [
  {
    type: CAMERA_FLY_PRESET_TYPES.CIRCLE,
    name: 'Circle',
  },
  {
    type: CAMERA_FLY_PRESET_TYPES.DRONIE,
    name: 'Dronie',
  },
  {
    type: CAMERA_FLY_PRESET_TYPES.HELIX,
    name: 'Helix',
  },
  {
    type: CAMERA_FLY_PRESET_TYPES.ROCKET,
    name: 'Rocket',
  },
];

const MoveMainCameraParams: React.FC<MoveParamsProps> = ({
  nodeId,
  params,
  setParams,
}) => {
  const [selectedPreset, setSelectedPreset] =
    useState<CAMERA_FLY_PRESET_TYPES | null>(null);

  useEffect(() => {
    params.flyPresetType && setSelectedPreset(params.flyPresetType);
  }, []);

  function onSelectPositionPoint(propertyName: string, propertyKey: string) {
    window.dispatchEvent(
      new CustomEvent('add_position_capture_point', {
        detail: {
          nodeId,
          actionName: 'Move Main Camera',
          propertyName,
          propertyKey,
        },
      })
    );
  }

  return (
    <div className={styles.parameterContainer}>
      <Space direction='vertical' style={{ width: '100%' }}>
        {!selectedPreset && (
          <>
            <h1 className={styles.h1Label}>
              <span>From position</span>
              <Button
                className={styles.selectPosButton}
                onClick={() =>
                  onSelectPositionPoint('From position', 'fromPosition')
                }
              >
                Select Point
              </Button>
            </h1>
            <span className={styles.label}>Position X:</span>
            <InputNumber
              value={params.fromPosition?.x || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  fromPosition: {
                    ...params.fromPosition,
                    x: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Y:</span>
            <InputNumber
              value={params.fromPosition?.y || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  fromPosition: {
                    ...params.fromPosition,
                    y: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Z:</span>
            <InputNumber
              value={params.fromPosition?.z || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  fromPosition: {
                    ...params.fromPosition,
                    z: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <h1 className={styles.h1Label}>
              <span>To position</span>
              <Button
                className={styles.selectPosButton}
                onClick={() =>
                  onSelectPositionPoint('To position', 'toPosition')
                }
              >
                Select Point
              </Button>
            </h1>
            <span className={styles.label}>Position X:</span>
            <InputNumber
              value={params.toPosition?.x || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  toPosition: {
                    ...params.toPosition,
                    x: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Y:</span>
            <InputNumber
              value={params.toPosition?.y || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  toPosition: {
                    ...params.toPosition,
                    y: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Z:</span>
            <InputNumber
              value={params.toPosition?.z || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  toPosition: {
                    ...params.toPosition,
                    z: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
          </>
        )}
        <div style={{ marginTop: '.5rem' }}>
          <h3>Show Look At Target:</h3>
          <Switch
            value={params.enableLookAtTarget || ''}
            onChange={(value) => {
              if (!value) {
                setSelectedPreset(null);
                setParams({ ...params, flyPresetType: null });
              }
              setParams({ ...params, enableLookAtTarget: value });
            }}
          />
        </div>
        {params.enableLookAtTarget && (
          <div style={{ marginTop: '.5rem' }}>
            <h3>Select fly preset:</h3>
            <div className={styles.presetContainer}>
              {FLY_PRESETS.map((el) => (
                <div
                  key={el.type}
                  onClick={() => {
                    if (el.type === selectedPreset) {
                      setSelectedPreset(null);
                      setParams({ ...params, flyPresetType: null });
                      return;
                    }
                    setSelectedPreset(el.type);
                    setParams({ ...params, flyPresetType: el.type });
                  }}
                  className={
                    selectedPreset === el.type
                      ? styles.presetCardActive
                      : styles.presetCard
                  }
                >
                  {el.name}
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedPreset && (
          <div style={{ marginTop: '.5rem' }}>
            <h1 className={styles.h1Label}>Advanced Preset settings:</h1>
            {selectedPreset === CAMERA_FLY_PRESET_TYPES.CIRCLE && (
              <>
                <span className={styles.label}>Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.CIRCLE]
                      ?.radius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.CIRCLE
                    ].radius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.CIRCLE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.CIRCLE
                          ],
                          radius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.CIRCLE]
                      ?.height ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.CIRCLE
                    ].height
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.CIRCLE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.CIRCLE
                          ],
                          height: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
              </>
            )}
            {selectedPreset === CAMERA_FLY_PRESET_TYPES.DRONIE && (
              <>
                <span className={styles.label}>Initial Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.DRONIE]
                      ?.radius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.DRONIE
                    ].radius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.DRONIE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.DRONIE
                          ],
                          radius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Initial Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.DRONIE]
                      ?.height ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.DRONIE
                    ].height
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.DRONIE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.DRONIE
                          ],
                          height: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span className={styles.label}>Final Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.DRONIE]
                      ?.toRadius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.DRONIE
                    ].toRadius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.DRONIE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.DRONIE
                          ],
                          toRadius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Final Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.DRONIE]
                      ?.toHeight ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.DRONIE
                    ].toHeight
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.DRONIE]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.DRONIE
                          ],
                          toHeight: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
              </>
            )}
            {selectedPreset === CAMERA_FLY_PRESET_TYPES.HELIX && (
              <>
                <span className={styles.label}>Initial Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.HELIX]
                      ?.radius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.HELIX
                    ].radius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.HELIX]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.HELIX
                          ],
                          radius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Initial Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.HELIX]
                      ?.height ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.HELIX
                    ].height
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.HELIX]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.HELIX
                          ],
                          height: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span className={styles.label}>Final Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.HELIX]
                      ?.toRadius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.HELIX
                    ].toRadius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.HELIX]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.HELIX
                          ],
                          toRadius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Final Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.HELIX]
                      ?.toHeight ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.HELIX
                    ].toHeight
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.HELIX]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.HELIX
                          ],
                          toHeight: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
              </>
            )}
            {selectedPreset === CAMERA_FLY_PRESET_TYPES.ROCKET && (
              <>
                <span className={styles.label}>Initial Radius:</span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.ROCKET]
                      ?.radius ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.ROCKET
                    ].radius
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.ROCKET]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.ROCKET
                          ],
                          radius: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Initial Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.ROCKET]
                      ?.height ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.ROCKET
                    ].height
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.ROCKET]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.ROCKET
                          ],
                          height: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
                <span
                  className={styles.label}
                  style={{ display: 'inline-block', marginTop: 10 }}
                >
                  Final Height:
                </span>
                <InputNumber
                  value={
                    params.presetSettings[CAMERA_FLY_PRESET_TYPES.ROCKET]
                      ?.toHeight ||
                    DEFAULT_PRESET_ADVANCED_SETTINGS[
                      CAMERA_FLY_PRESET_TYPES.ROCKET
                    ].toHeight
                  }
                  min={2}
                  onChange={(value) =>
                    setParams({
                      ...params,
                      presetSettings: {
                        ...params.presetSettings,
                        [CAMERA_FLY_PRESET_TYPES.ROCKET]: {
                          ...params.presetSettings[
                            CAMERA_FLY_PRESET_TYPES.ROCKET
                          ],
                          toHeight: value,
                        },
                      },
                    })
                  }
                  style={{ width: '100%' }}
                />
              </>
            )}
          </div>
        )}
        {!selectedPreset && (
          <>
            <h1 className={styles.h1Label}>Easing:</h1>
            <Select
              value={params.easing || 'linear'}
              onChange={(value) => setParams({ ...params, easing: value })}
              style={{ width: '100%' }}
            >
              <Option value={EASING_TYPES.LINEAR}>Linear</Option>
              <Option value={EASING_TYPES.EASE_IN}>Ease In</Option>
              <Option value={EASING_TYPES.EASE_OUT}>Ease Out</Option>
              <Option value={EASING_TYPES.EASE_IN_OUT}>Ease In Out</Option>
            </Select>
          </>
        )}
      </Space>
    </div>
  );
};

export default MoveMainCameraParams;
